import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const GA_MEASUREMENT_ID = "G-3SP7NPYMVV";

/**
 * Load GA4 once per app lifecycle
 * This is what triggers session_start
 */
function loadGtagOnce() {
  if (!GA_MEASUREMENT_ID || window.gtag) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer!.push(arguments);
  };

  const script = document.createElement("script");
  script.id = "ga4-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.gtag("js", new Date());

  // 🔑 SESSION START
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
  });
}

/**
 * GA4 SPA Tracking Hook
 */
export default function useGoogleAnalytics() {
  const location = useLocation();
  const initialized = useRef(false);
  const lastPath = useRef<string | null>(null);
  const pageStartTime = useRef<number>(Date.now());

  /** Load GA once */
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    loadGtagOnce();
  }, []);

  /** Track page views + time on page */
  useEffect(() => {
    if (!window.gtag) return;

    const now = Date.now();

    // Time on previous page
    if (lastPath.current) {
      window.gtag("event", "page_time", {
        page_path: lastPath.current,
        duration_ms: now - pageStartTime.current,
      });
    }

    pageStartTime.current = now;
    lastPath.current = location.pathname;

    // SPA page_view
    window.gtag("event", "page_view", {
      page_path: location.pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname]);

  /** Track clicks + outbound links */
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!window.gtag) return;

      const target = e.target as HTMLElement;
      const anchor = target.closest("a") as HTMLAnchorElement | null;

      if (!anchor || !anchor.href) return;

      const isExternal =
        !anchor.href.startsWith(window.location.origin);

      window.gtag("event", "click", {
        link_text: anchor.innerText || anchor.href,
        link_url: anchor.href,
        outbound: isExternal,
      });
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  /** Track scroll depth */
  useEffect(() => {
    let maxScroll = 0;

    function handleScroll() {
      const scrollPercent = Math.round(
        ((window.scrollY + window.innerHeight) /
          document.documentElement.scrollHeight) *
        100
      );

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;

        if ([25, 50, 75, 90].includes(scrollPercent)) {
          window.gtag?.("event", "scroll_depth", {
            percent: scrollPercent,
            page_path: location.pathname,
          });
        }
      }
    }

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  /** Track page exit / tab close */
  useEffect(() => {
    function handleUnload() {
      if (!window.gtag || !lastPath.current) return;

      window.gtag("event", "page_exit", {
        page_path: lastPath.current,
        duration_ms: Date.now() - pageStartTime.current,
      });
    }

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);
}
